import { useState, useEffect } from "react";
import { deadlineOverride } from "../lib/utils";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import PublicBrowseShell from "../components/PublicBrowseShell";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MagnifyingGlass, FunnelSimple, SortAscending, SortDescending, CurrencyDollar, Calendar, Bookmark, BookmarkSimple, Lightning, Sparkle, Lock, Crown, TrendUp, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PrepKitModal } from "../components/PrepKitModal";
import WinProbabilityBadge from "../components/WinProbabilityBadge";

const Grants = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [grants, setGrants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [discovering, setDiscovering] = useState(false);
  const [grantsBlocked, setGrantsBlocked] = useState(false);
  const [appsUsed, setAppsUsed] = useState(0);
  const [appsLimit, setAppsLimit] = useState(3);
  const [prepKitGrant, setPrepKitGrant] = useState(null);
  
  // Filters — default to newest sort if URL param present
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [audience, setAudience] = useState(searchParams.get("audience") || "nonprofit");
  const [minAmount, setMinAmount] = useState(searchParams.get("min") || "");
  const [maxAmount, setMaxAmount] = useState(searchParams.get("max") || "");
  const [sortBy, setSortBy] = useState(
    searchParams.get("sort") === "newest" ? "created_at" : 
    searchParams.get("sort") === "deadline" ? "deadline" : "best_match"
  );
  const [sortOrder, setSortOrder] = useState(searchParams.get("sort") === "newest" ? "desc" : "desc");
  // Persist page in URL so navigating to a grant detail + back restores the page
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10) || 1);
  // Verified-only filter — built after the June 17, 2026 Chick-fil-A data incident.
  // When ON, only grants an admin has personally checked against the funder's
  // website are returned. Off by default so the catalog stays wide.
  const [verifiedOnly, setVerifiedOnly] = useState(searchParams.get("v") === "1");

  // Sync state → URL whenever any filter or the page changes.
  // Uses { replace: true } so we don't pollute browser history with every keystroke.
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category) params.set("category", category);
    if (audience && audience !== "nonprofit") params.set("audience", audience);
    if (minAmount) params.set("min", minAmount);
    if (maxAmount) params.set("max", maxAmount);
    if (sortBy === "created_at") params.set("sort", "newest");
    else if (sortBy === "deadline") params.set("sort", "deadline");
    if (page > 1) params.set("page", String(page));
    if (verifiedOnly) params.set("v", "1");
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, audience, minAmount, maxAmount, sortBy, page, verifiedOnly]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/grants/categories`);
        setCategories(res.data.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        const res = await axios.get(`${API}/bookmarks`);
        const ids = new Set((res.data.bookmarks || []).map(g => g.grant_id));
        setBookmarkedIds(ids);
      } catch (error) {
        console.error("Error fetching bookmarks:", error);
      }
    };
    fetchBookmarks();
  }, []);

  useEffect(() => {
    const fetchGrants = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.append("search", search);
        if (category) params.append("category", category);
        if (audience) params.append("audience", audience);
        if (minAmount) params.append("min_amount", minAmount);
        if (maxAmount) params.append("max_amount", maxAmount);
        params.append("sort_by", sortBy);
        params.append("sort_order", sortOrder);
        params.append("page", page);
        params.append("limit", 12);
        if (verifiedOnly) params.append("verified_only", "true");

        const res = await axios.get(`${API}/grants?${params.toString()}`);
        setGrants(res.data.grants || []);
        setTotalPages(res.data.pages || 1);
        setGrantsBlocked(res.data.grants_blocked || false);
        setAppsUsed(res.data.apps_used || 0);
        setAppsLimit(res.data.apps_limit || 3);
      } catch (error) {
        console.error("Error fetching grants:", error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchGrants, 300);
    return () => clearTimeout(debounce);
  }, [search, category, audience, minAmount, maxAmount, sortBy, sortOrder, page, verifiedOnly]);

  const toggleBookmark = async (e, grantId) => {
    e.stopPropagation();
    try {
      if (bookmarkedIds.has(grantId)) {
        await axios.delete(`${API}/bookmarks/${grantId}`);
        setBookmarkedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(grantId);
          return newSet;
        });
      } else {
        await axios.post(`${API}/bookmarks/${grantId}`, {});
        setBookmarkedIds(prev => new Set([...prev, grantId]));
      }
    } catch (error) {
      console.error("Error toggling bookmark:", error);
    }
  };

  const formatDeadline = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return "Expired";
    if (diffDays === 0) return "Due today";
    if (diffDays === 1) return "Due tomorrow";
    if (diffDays <= 7) return `${diffDays} days left`;
    return date.toLocaleDateString();
  };

  const getDeadlineColor = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "text-red-500";
    if (diffDays <= 1) return "text-red-400";
    if (diffDays <= 3) return "text-orange-400";
    if (diffDays <= 7) return "text-yellow-400";
    return "text-zinc-500";
  };

  const isNewGrant = (createdAt) => {
    if (!createdAt) return false;
    const created = new Date(createdAt);
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return created > cutoff;
  };

  const getCompetitionLevel = (grant) => {
    const rate = grant.acceptance_rate || 0;
    if (rate >= 40) return { label: "Low Competition", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" };
    if (rate >= 25) return { label: "Medium Competition", color: "text-amber-400 bg-amber-500/10 border-amber-500/30" };
    return { label: "High Competition", color: "text-red-400 bg-red-500/10 border-red-500/30" };
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const res = await axios.post(`${API}/grants/discover`, {});
      const count = res.data.count || 0;
      toast.success(`Discovered ${count} new grant${count !== 1 ? 's' : ''}!`);
      // Switch to newest sort to show the new grants
      setSortBy("created_at");
      setSortOrder("desc");
      setPage(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to discover grants");
    } finally {
      setDiscovering(false);
    }
  };

  const Shell = user ? DashboardLayout : PublicBrowseShell;

  return (
    <Shell>
      <div className="space-y-6" data-testid="grants-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Browse Grants</h1>
            <p className="text-zinc-400 mt-1">Real grant opportunities for your nonprofit</p>
          </div>
        </div>

        {/* Free Tier Usage Indicator */}
        {user?.subscription_tier !== "pro" && user?.role !== "admin" && (
          <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-sm px-4 py-2.5" data-testid="apps-usage-bar">
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-400">Applications this month:</span>
              <div className="flex gap-1">
                {[...Array(appsLimit)].map((_, i) => (
                  <div key={i} className={`w-3 h-3 rounded-full ${i < appsUsed ? 'bg-emerald-500' : 'bg-zinc-800 border border-zinc-700'}`} />
                ))}
              </div>
              <span className="text-sm font-medium">{appsUsed}/{appsLimit}</span>
            </div>
            {appsUsed >= appsLimit - 1 && (
              <Button size="sm" onClick={() => navigate("/pricing")} className="bg-emerald-500 text-black text-xs font-bold hover:bg-emerald-400 rounded-sm" data-testid="upgrade-from-grants-btn">
                <Crown className="w-3.5 h-3.5 mr-1" />
                Upgrade to Premium
              </Button>
            )}
          </div>
        )}

        {/* Grants Blocked State */}
        {grantsBlocked ? (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-12 text-center">
              <Lock className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Monthly Limit Reached</h2>
              <p className="text-zinc-400 mb-6 max-w-md mx-auto">
                You've used all {appsLimit} applications for this month. Upgrade to Premium for unlimited applications and access to all grants.
              </p>
              <Button onClick={() => navigate("/pricing")} className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm px-8" data-testid="upgrade-blocked-btn">
                <Crown className="w-5 h-5 mr-2" />
                Upgrade to Premium — $49.99/month
              </Button>
              <p className="text-xs text-zinc-600 mt-4">Your limit resets at the start of next month</p>
            </CardContent>
          </Card>
        ) : (
        <>
        {/* Audience switcher (Nonprofit / Small Business / Startup) */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-zinc-400 mr-2 font-semibold">I'm looking for grants for:</span>
              {[
                { value: "nonprofit", label: "Nonprofits" },
                { value: "small_business", label: "Small Businesses" },
                { value: "startup", label: "Startups" },
                { value: "farmer", label: "Farmers" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { setAudience(opt.value); setPage(1); }}
                  className={`px-4 py-2 rounded-md text-sm font-semibold border transition ${
                    audience === opt.value
                      ? "bg-emerald-500 text-black border-emerald-500"
                      : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-emerald-700"
                  }`}
                  data-testid={`audience-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Search & Filters */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input 
                  placeholder="Search grants..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-10 bg-zinc-900 border-zinc-800 focus:border-emerald-500"
                  data-testid="grant-search-input"
                />
              </div>

              {/* Category Filter */}
              <Select value={category || "all"} onValueChange={(v) => { setCategory(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger className="w-full md:w-48 bg-zinc-900 border-zinc-800" data-testid="category-filter">
                  <FunnelSimple className="w-4 h-4 mr-2 text-zinc-500" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Amount Range */}
              <div className="flex items-center gap-2">
                <Input 
                  placeholder="Min $"
                  type="number"
                  value={minAmount}
                  onChange={(e) => { setMinAmount(e.target.value); setPage(1); }}
                  className="w-24 bg-zinc-900 border-zinc-800"
                  data-testid="min-amount-input"
                />
                <span className="text-zinc-500">-</span>
                <Input 
                  placeholder="Max $"
                  type="number"
                  value={maxAmount}
                  onChange={(e) => { setMaxAmount(e.target.value); setPage(1); }}
                  className="w-24 bg-zinc-900 border-zinc-800"
                  data-testid="max-amount-input"
                />
              </div>

              {/* Sort */}
              <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
                <SelectTrigger className="w-full md:w-40 bg-zinc-900 border-zinc-800" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="best_match">Best Match</SelectItem>
                  <SelectItem value="deadline">Deadline</SelectItem>
                  <SelectItem value="amount_max">Amount</SelectItem>
                  <SelectItem value="created_at">Newest</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                className="text-zinc-400 hover:text-white"
                data-testid="sort-order-btn"
              >
                {sortOrder === "asc" ? <SortAscending className="w-5 h-5" /> : <SortDescending className="w-5 h-5" />}
              </Button>
            </div>

            {/* Verified Only + Reset Filters */}
            <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 text-xs text-zinc-400 max-w-2xl">
                <Lightning weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>
                  <b className="text-zinc-200">Win Probability</b> shows your realistic shot at each grant based on your profile, the funder's acceptance rate, and your fit. Every match — Strong, Possible, or Long Shot — stays visible so you can decide for yourself.
                </span>
              </div>
              <button
                type="button"
                onClick={() => { setVerifiedOnly(!verifiedOnly); setPage(1); }}
                className={`group flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-semibold uppercase tracking-wider border transition-colors ${
                  verifiedOnly
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20"
                    : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500"
                }`}
                data-testid="verified-only-toggle"
                title="Only show grants we have personally verified against the funder&apos;s website"
                aria-pressed={verifiedOnly}
              >
                <span className={`w-2 h-2 rounded-full ${verifiedOnly ? "bg-emerald-400" : "bg-zinc-600"}`} />
                Verified Only
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setCategory("all");
                  setAudience("");
                  setMinAmount("");
                  setMaxAmount("");
                  setSortBy("best_match");
                  setSortOrder("desc");
                  setPage(1);
                  setVerifiedOnly(false);
                  setSearchParams({}, { replace: true });
                }}
                className="group flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-semibold uppercase tracking-wider border transition-colors bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white hover:border-emerald-500/50"
                data-testid="reset-filters-btn"
                title="Clear every filter and search"
              >
                Reset Filters
              </button>
            </div>
          </CardContent>
        </Card>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="loading-spinner" />
          </div>
        ) : grants.length === 0 ? (
          <div className="text-center py-12 max-w-2xl mx-auto" data-testid="grants-empty-state">
            <MagnifyingGlass className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              No grants found.
            </h3>
            <p className="text-zinc-500 mb-6">
              Try adjusting your search or filters.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {(search || category || minAmount || maxAmount) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch("");
                    setCategory("");
                    setMinAmount("");
                    setMaxAmount("");
                    setPage(1);
                  }}
                  className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                  data-testid="empty-clear-filters-btn"
                >
                  Clear all filters
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate("/profile")}
                className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                data-testid="empty-update-profile-btn"
              >
                Update my profile
              </Button>
            </div>
            <p className="text-xs text-zinc-600 mt-8 leading-relaxed">
              Tip: the more focus areas, county, and budget detail in your profile, the
              more accurately we can match. Niche-mission orgs often see better results
              after broadening one filter at a time.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {grants.map((grant) => (
                <Card 
                  key={grant.grant_id}
                  className="bg-zinc-950 border-zinc-800 card-hover cursor-pointer relative"
                  onClick={() => navigate(`/grants/${grant.grant_id}`)}
                  data-testid={`grant-card-${grant.grant_id}`}
                >
                  <CardContent className="p-6">
                    {/* Bookmark Button */}
                    <button
                      onClick={(e) => toggleBookmark(e, grant.grant_id)}
                      className="absolute top-4 right-4 p-2 rounded-sm hover:bg-zinc-800 transition-colors"
                      data-testid={`bookmark-btn-${grant.grant_id}`}
                    >
                      {bookmarkedIds.has(grant.grant_id) ? (
                        <Bookmark weight="fill" className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <BookmarkSimple className="w-5 h-5 text-zinc-500 hover:text-white" />
                      )}
                    </button>

                    {/* Category + NEW Badge + National Badge + Match Score */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="badge-emerald rounded-sm text-xs px-2 py-0.5 inline-block">
                        {grant.category}
                      </span>
                      {grant.scope === "national" && (
                        <span
                          className="bg-blue-500/20 text-blue-300 border border-blue-500/40 text-xs font-bold px-2 py-0.5 rounded-sm inline-flex items-center gap-1"
                          data-testid={`national-badge-${grant.grant_id}`}
                          title="National grant — open to SC applicants"
                        >
                          <span aria-hidden>🌎</span> NATIONAL
                        </span>
                      )}
                      {isNewGrant(grant.created_at) && (
                        <span className="bg-emerald-500 text-black text-xs font-bold px-2 py-0.5 rounded-sm animate-pulse" data-testid={`new-badge-${grant.grant_id}`}>
                          NEW
                        </span>
                      )}
                      {grant.match_score != null && (
                        <span 
                          className={`text-xs font-bold px-2 py-0.5 rounded-sm ${
                            grant.match_score >= 70 
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                              : grant.match_score >= 40 
                                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                                : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                          }`}
                          data-testid={`match-score-${grant.grant_id}`}
                        >
                          {grant.match_score}% Match
                        </span>
                      )}
                      {grant.win_probability && (
                        <div className="ml-auto">
                          <WinProbabilityBadge winProbability={grant.win_probability} size="sm" />
                        </div>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-semibold mb-2 pr-8">{grant.title}</h3>

                    {/* Verified badge — flag for grants admin-checked against the funder's website */}
                    {grant.last_verified_at && (
                      <div
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded-sm mb-2"
                        title={`Manually verified on ${new Date(grant.last_verified_at).toLocaleDateString()}`}
                        data-testid={`grant-verified-${grant.grant_id}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        Verified · {new Date(grant.last_verified_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </div>
                    )}
                    
                    {/* Description */}
                    <p className="text-sm text-zinc-400 mb-4 line-clamp-2">
                      {grant.description}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1 text-emerald-400 font-mono">
                        <CurrencyDollar className="w-4 h-4" />
                        <span>{!grant.amount_min && !grant.amount_max
                          ? "See listing"
                          : !grant.amount_min
                            ? `Up to $${grant.amount_max.toLocaleString()}`
                            : grant.amount_min === grant.amount_max
                              ? `$${grant.amount_max.toLocaleString()}`
                              : `$${grant.amount_min.toLocaleString()} - $${grant.amount_max.toLocaleString()}`}</span>
                      </div>
                      <div className={`flex items-center gap-1 ${getDeadlineColor(grant.deadline)}`}>
                        <Calendar className="w-4 h-4" />
                        <span data-testid="grant-deadline-label">{deadlineOverride(grant) || formatDeadline(grant.deadline)}</span>
                      </div>
                    </div>

                    {/* Source + Competition Level & Applicants */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800/50">
                      <div className="flex items-center gap-2">
                        {grant.source === "grants.gov" && (
                          <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-sm px-1.5 py-0.5">Federal</span>
                        )}
                        {grant.acceptance_rate > 0 && (
                          <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-sm border ${getCompetitionLevel(grant).color}`} data-testid={`competition-${grant.grant_id}`}>
                            <TrendUp className="w-3.5 h-3.5" />
                            {getCompetitionLevel(grant).label}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-zinc-500 flex items-center gap-1 ml-auto" data-testid={`applicants-${grant.grant_id}`}>
                        <Users className="w-3.5 h-3.5" />
                        {grant.application_count || 0} applicant{grant.application_count !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {/* Quick Apply */}
                    {grant.funder_website ? (
                      <Button 
                        className="w-full mt-4 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPrepKitGrant(grant);
                        }}
                        data-testid={`apply-btn-${grant.grant_id}`}
                      >
                        <Lightning className="w-4 h-4 mr-2" />
                        Apply Now
                      </Button>
                    ) : (
                      <Button 
                        className="w-full mt-4 bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/grants/${grant.grant_id}`);
                        }}
                        data-testid={`apply-btn-${grant.grant_id}`}
                      >
                        <Lightning className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8 pb-16">
                <Button 
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="border-zinc-800"
                  data-testid="prev-page-btn"
                >
                  Previous
                </Button>
                <span className="text-sm text-zinc-400 px-4">
                  Page {page} of {totalPages}
                </span>
                <Button 
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="border-zinc-800"
                  data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
        </>
        )}
      </div>

      {/* Prep Kit Modal */}
      {prepKitGrant && (
        <PrepKitModal 
          grant={prepKitGrant} 
          open={!!prepKitGrant} 
          onOpenChange={(open) => { if (!open) setPrepKitGrant(null); }} 
        />
      )}
    </Shell>
  );
};

export default Grants;
